import Title from '@/components/ui/Title';
import { useMemo } from 'react';
import { CircleAlert, Plus, X } from 'lucide-react';
import { VariableChunkEditor } from '@/components/ui/VariableChunkEditor';
import type { Chunk as VCEChunk } from '@/components/ui/VariableChunkEditor';
import { useTranslation } from 'react-i18next';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import type { UseFormReturn } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion2';
import type { PurchaseNotificationFormValues } from './PopUpsSection';
import { LucideIconPopoverPicker, LucideLazyIcon } from '../status/LucideIconPopoverPicker';
import dynamicIconImports from 'lucide-react/dynamicIconImports';
import ColorSelector from '@/components/ui/ColorSelector';

type Chunk =
  | { type: 'text'; value: string; id: string }
  | { type: 'field'; value: string; id: string };

type Variant = {
  id: string;
  chunks: Chunk[];
  selectedIcon: string;
  showIcon: boolean;
};

interface PopUpBuyerProps {
  purchaseNotificationForm: UseFormReturn<PurchaseNotificationFormValues>;
  visualIdentity: WorkspaceVisualIdentity | null | undefined;
  isPublic?: boolean;
  lengthFn?: (chunks: Array<{ type: 'text' | 'field'; value: string; id: string }>) => number;
}

export default function PopUpBuyer({
  purchaseNotificationForm,
  visualIdentity,
  isPublic,
  lengthFn,
}: PopUpBuyerProps) {
  const { t } = useTranslation('createWebinar');

  const publicVariants = purchaseNotificationForm.watch('publicVariants');
  const privateChunks = purchaseNotificationForm.watch('privateChunks');

  const formErrors = purchaseNotificationForm.formState.errors;

  const privateChunksError = formErrors.privateChunks?.message;

  const getVariantError = (variantIndex: number) => {
    if (formErrors.publicVariants && Array.isArray(formErrors.publicVariants)) {
      const variantError = formErrors.publicVariants[variantIndex];
      return variantError?.chunks?.message || variantError?.message;
    }
    if (formErrors.publicVariants?.message) {
      return formErrors.publicVariants.message;
    }
    return undefined;
  };

  const variants: Variant[] = isPublic
    ? publicVariants.map((v) => ({
        id: v.id,
        chunks: v.chunks as Chunk[],
        selectedIcon: v.selectedIcon,
        showIcon: v.showIcon,
      }))
    : [
        {
          id: 'private-variant',
          chunks: privateChunks as Chunk[],
          selectedIcon: purchaseNotificationForm.watch('privateIcon') || '',
          showIcon: purchaseNotificationForm.watch('displayPrivateIcon') || false,
        },
      ];

  const currentVariant = variants[0];
  const chunks = currentVariant.chunks;

  const updateVariant = (variantId: string, updater: (variant: Variant) => Variant) => {
    if (isPublic) {
      const currentVariants = purchaseNotificationForm.getValues('publicVariants');
      const updatedVariants = currentVariants.map((variant) => {
        if (variant.id === variantId) {
          const updated = updater({
            id: variant.id,
            chunks: variant.chunks as Chunk[],
            selectedIcon: variant.selectedIcon,
            showIcon: variant.showIcon,
          });
          return {
            id: updated.id,
            chunks: updated.chunks,
            selectedIcon: updated.selectedIcon,
            showIcon: updated.showIcon,
          };
        }
        return variant;
      });
      purchaseNotificationForm.setValue('publicVariants', updatedVariants);
    }
  };

  const addVariant = () => {
    if (isPublic) {
      const currentVariants = purchaseNotificationForm.getValues('publicVariants');
      if (currentVariants.length < 4) {
        const firstVariant = currentVariants[0];
        const newVariants = [
          ...currentVariants,
          {
            id: crypto.randomUUID(),
            chunks: [{ type: 'text' as const, value: '', id: crypto.randomUUID() }],
            selectedIcon: firstVariant?.selectedIcon || '',
            showIcon: firstVariant?.showIcon || false,
          },
        ];
        purchaseNotificationForm.setValue('publicVariants', newVariants);
      }
    }
  };

  const removeVariant = (variantId: string) => {
    if (isPublic) {
      const currentVariants = purchaseNotificationForm.getValues('publicVariants');
      if (currentVariants.length > 1) {
        const filteredVariants = currentVariants.filter((variant) => variant.id !== variantId);
        purchaseNotificationForm.setValue('publicVariants', filteredVariants);
      }
    }
  };

  const addField = (fieldValue: string, variantId: string = currentVariant.id) => {
    if (isPublic) {
      updateVariant(variantId, (variant) => {
        let newChunks = [...variant.chunks];
        if (newChunks.length > 0 && newChunks[0].type === 'text' && newChunks[0].value === '') {
          newChunks.shift();
        }
        newChunks.push({ type: 'field', value: fieldValue, id: crypto.randomUUID() });
        newChunks.push({ type: 'text', value: '', id: crypto.randomUUID() });
        return { ...variant, chunks: newChunks };
      });
    } else {
      const currentChunks = purchaseNotificationForm.getValues('privateChunks');
      let newChunks = [...currentChunks];
      if (newChunks.length > 0 && newChunks[0].type === 'text' && newChunks[0].value === '') {
        newChunks.shift();
      }
      newChunks.push({ type: 'field', value: fieldValue, id: crypto.randomUUID() });
      newChunks.push({ type: 'text', value: '', id: crypto.randomUUID() });
      purchaseNotificationForm.setValue('privateChunks', newChunks);
    }
  };

  const removeField = (fieldId: string, variantId: string = currentVariant.id) => {
    const processChunks = (chunks: Chunk[]) => {
      const fieldIndex = chunks.findIndex((chunk) => chunk.id === fieldId);
      if (fieldIndex === -1) return chunks;

      const newChunks = [...chunks];
      const beforeField = chunks[fieldIndex - 1];
      const afterField = chunks[fieldIndex + 1];

      if (beforeField?.type === 'text' && afterField?.type === 'text') {
        const mergedChunk: Chunk = {
          type: 'text',
          value: beforeField.value + afterField.value,
          id: beforeField.id,
        };
        newChunks.splice(fieldIndex - 1, 3, mergedChunk);
      } else {
        newChunks.splice(fieldIndex, 1);
      }

      const hasTextChunk = newChunks.some((chunk) => chunk.type === 'text');
      if (!hasTextChunk) {
        newChunks.push({
          type: 'text',
          value: '',
          id: crypto.randomUUID(),
        });
      }

      if (newChunks.length > 0 && newChunks[0].type === 'field') {
        const firstTextChunkIndex = newChunks.findIndex((c) => c.type === 'text');
        if (
          firstTextChunkIndex === -1 ||
          (firstTextChunkIndex > 0 && newChunks[firstTextChunkIndex].value !== '')
        ) {
          newChunks.unshift({
            type: 'text',
            value: '',
            id: crypto.randomUUID(),
          });
        }
      }

      return newChunks;
    };

    if (isPublic) {
      updateVariant(variantId, (variant) => ({
        ...variant,
        chunks: processChunks(variant.chunks),
      }));
    } else {
      const currentChunks = purchaseNotificationForm.getValues('privateChunks');
      const updatedChunks = processChunks(currentChunks as Chunk[]);
      purchaseNotificationForm.setValue('privateChunks', updatedChunks);
    }
  };

  const removePreviousFieldWhenChunkEmpty = (
    chunkId: string,
    variantId: string = currentVariant.id,
  ) => {
    const processChunks = (chunks: Chunk[]) => {
      const textChunkIndex = chunks.findIndex(
        (chunk) => chunk.id === chunkId && chunk.type === 'text',
      );
      if (textChunkIndex <= 0 || chunks[textChunkIndex].value !== '') return chunks;

      const updatedChunks = [...chunks];
      const previousChunk = updatedChunks[textChunkIndex - 1];
      if (previousChunk.type !== 'field') return chunks;

      const beforeField = updatedChunks[textChunkIndex - 2];
      const afterField = updatedChunks[textChunkIndex + 1];

      if (beforeField?.type === 'text' && afterField?.type === 'text') {
        const mergedChunk: Chunk = {
          type: 'text',
          value: beforeField.value + afterField.value,
          id: beforeField.id,
        };
        updatedChunks.splice(textChunkIndex - 2, 4, mergedChunk);
      } else if (beforeField?.type === 'text') {
        updatedChunks.splice(textChunkIndex - 2, 3, beforeField);
      } else if (afterField?.type === 'text') {
        updatedChunks.splice(textChunkIndex - 1, 2, afterField);
      } else {
        updatedChunks.splice(textChunkIndex - 1, 2);
      }

      if (!updatedChunks.some((chunk) => chunk.type === 'text')) {
        updatedChunks.push({ type: 'text', value: '', id: crypto.randomUUID() });
      }

      if (updatedChunks.length > 0 && updatedChunks[0].type === 'field') {
        const firstTextChunkIndex = updatedChunks.findIndex((c) => c.type === 'text');
        if (
          firstTextChunkIndex === -1 ||
          (firstTextChunkIndex > 0 && updatedChunks[firstTextChunkIndex].value !== '')
        ) {
          updatedChunks.unshift({ type: 'text', value: '', id: crypto.randomUUID() });
        }
      }

      return updatedChunks;
    };

    if (isPublic) {
      updateVariant(variantId, (variant) => ({
        ...variant,
        chunks: processChunks(variant.chunks),
      }));
    } else {
      const currentChunks = purchaseNotificationForm.getValues('privateChunks');
      const updatedChunks = processChunks(currentChunks as Chunk[]);
      purchaseNotificationForm.setValue('privateChunks', updatedChunks);
    }
  };

  const updateTextChunk = (
    chunkId: string,
    newValue: string,
    variantId: string = currentVariant.id,
  ) => {
    const processChunks = (chunks: Chunk[]) =>
      chunks.map((chunk) =>
        chunk.id === chunkId && chunk.type === 'text' ? { ...chunk, value: newValue } : chunk,
      );

    if (isPublic) {
      updateVariant(variantId, (variant) => ({
        ...variant,
        chunks: processChunks(variant.chunks),
      }));
    } else {
      const currentChunks = purchaseNotificationForm.getValues('privateChunks');
      const updatedChunks = processChunks(currentChunks as Chunk[]);
      purchaseNotificationForm.setValue('privateChunks', updatedChunks);
    }
  };

  const updateVariantShowIcon = (variantId: string, show: boolean) => {
    if (isPublic) {
      updateVariant(variantId, (variant) => ({ ...variant, showIcon: show }));
      purchaseNotificationForm.setValue('displayPublicIcon', show);
    } else {
      purchaseNotificationForm.setValue('displayPrivateIcon', show);
    }
  };

  const updateAllVariantsIcon = (icon: string) => {
    if (isPublic) {
      const currentVariants = purchaseNotificationForm.getValues('publicVariants');
      const updatedVariants = currentVariants.map((variant) => ({
        ...variant,
        selectedIcon: icon,
      }));
      purchaseNotificationForm.setValue('publicVariants', updatedVariants);
    }
  };

  const updateAllVariantsShowIcon = (show: boolean) => {
    if (isPublic) {
      const currentVariants = purchaseNotificationForm.getValues('publicVariants');
      const updatedVariants = currentVariants.map((variant) => ({ ...variant, showIcon: show }));
      purchaseNotificationForm.setValue('publicVariants', updatedVariants);
      purchaseNotificationForm.setValue('displayPublicIcon', show);
    }
  };

  const getFullMessage = (variantChunks: Chunk[] = chunks) => {
    const raw = variantChunks
      .map((chunk, index) => {
        if (chunk.type === 'field') {
          const fieldValue = chunk.value.replace(/[\[\]]/g, '');
          const isFirst = index === 0;
          const isLast = index === variantChunks.length - 1;

          const previousChunk = index > 0 ? variantChunks[index - 1] : null;
          const nextChunk = index < variantChunks.length - 1 ? variantChunks[index + 1] : null;

          const prevEndsWithSpace =
            previousChunk?.type === 'text' && previousChunk.value.trimEnd() !== previousChunk.value;
          const nextStartsWithSpace =
            nextChunk?.type === 'text' && nextChunk.value.trimStart() !== nextChunk.value;
          const nextStartsWithSpecialChar =
            nextChunk?.type === 'text' &&
            nextChunk.value.trimStart() !== '' &&
            /^[.,!?;:)]/.test(nextChunk.value.trimStart());

          let result = fieldValue;

          if (isFirst) {
            if (!nextStartsWithSpace && !nextStartsWithSpecialChar) {
              result += ' ';
            }
          } else if (isLast) {
            if (!prevEndsWithSpace) {
              result = ' ' + result;
            }
          } else {
            if (!prevEndsWithSpace) {
              result = ' ' + result;
            }
            if (!nextStartsWithSpace && !nextStartsWithSpecialChar) {
              result += ' ';
            }
          }

          return result;
        }
        return chunk.value;
      })
      .join('');
    return raw.replace(/\s+([.,!?;:)]+)/g, '$1');
  };

  const prefix = isPublic ? 'public' : 'private';
  const colorsArray = useMemo(
    () => [
      {
        id: 'accent',
        name: t('popupForm.colorNames.accent'),
        value: visualIdentity?.accentColor,
        color: visualIdentity?.accentColor,
      },
      {
        id: 'background',
        name: t('popupForm.colorNames.background'),
        value: visualIdentity?.backgroundColor,
        color: visualIdentity?.backgroundColor,
      },
      {
        id: 'contrast',
        name: t('popupForm.colorNames.contrast'),
        value: visualIdentity?.contrastColor,
        color: visualIdentity?.contrastColor,
      },
      {
        id: 'gradient1-color1',
        name: t('popupForm.colorNames.gradient1'),
        value: visualIdentity?.gradient1Color1,
        color: visualIdentity?.gradient1Color1,
      },
      {
        id: 'gradient2-color1',
        name: t('popupForm.colorNames.gradient2'),
        value: visualIdentity?.gradient2Color1,
        color: visualIdentity?.gradient2Color1,
      },
      {
        id: 'secondary-background',
        name: t('popupForm.colorNames.secondaryBackground'),
        value: visualIdentity?.secondaryBackgroundColor,
        color: visualIdentity?.secondaryBackgroundColor,
      },
      { id: 'black', name: t('popupForm.colorNames.black'), value: '#000000', color: '#000000' },
      { id: 'white', name: t('popupForm.colorNames.white'), value: '#FFFFFF', color: '#FFFFFF' },
    ],
    [visualIdentity, t],
  );
  const arrayFontColors = useMemo(
    () => [
      { name: t('popupForm.colorNames.black'), value: '#000000' },
      { name: t('popupForm.colorNames.white'), value: '#FFFFFF' },
    ],
    [t],
  );

  const getColorValueById = (colorId: string, customColor?: string) => {
    if (colorId === 'custom' && customColor) return customColor;
    if (colorId === 'gradient1-color1' || colorId === 'gradient1-color2') {
      return visualIdentity?.gradient1Color1 ?? '#FFFFFF';
    }
    if (colorId === 'gradient2-color1' || colorId === 'gradient2-color2') {
      return visualIdentity?.gradient2Color1 ?? '#FFFFFF';
    }
    const color = colorsArray.find((c) => c.id === colorId);
    return color?.value ?? customColor ?? '#FFFFFF';
  };

  const bgColorId = isPublic
    ? purchaseNotificationForm.watch('publicBackgroundColor')
    : purchaseNotificationForm.watch('privateBackgroundColor');
  const customBgColor = isPublic
    ? purchaseNotificationForm.watch('customPublicBackgroundColor')
    : purchaseNotificationForm.watch('customPrivateBackgroundColor');
  const borderColorId = isPublic
    ? purchaseNotificationForm.watch('publicBorderColor')
    : purchaseNotificationForm.watch('privateBorderColor');
  const customBorderColor = isPublic
    ? purchaseNotificationForm.watch('customPublicBorderColor')
    : purchaseNotificationForm.watch('customPrivateBorderColor');

  const getBackgroundStyle = (colorId?: string, customColor?: string) => {
    const id = (colorId ?? bgColorId) as string;
    const custom = customColor ?? customBgColor ?? '';
    if (id === 'gradient1-color1' || id === 'gradient1-color2') {
      return {
        background: `linear-gradient(to bottom, ${visualIdentity?.gradient1Color1}, ${visualIdentity?.gradient1Color2})`,
      };
    }
    if (id === 'gradient2-color1' || id === 'gradient2-color2') {
      return {
        background: `linear-gradient(226deg, ${visualIdentity?.gradient2Color1} 0.2%, ${visualIdentity?.gradient2Color2} 104.63%)`,
      };
    }
    return { backgroundColor: getColorValueById(id, custom) };
  };

  const getBorderStyle = () => {
    if (borderColorId === 'no-border') return { border: 'none' };
    return {
      borderColor: getColorValueById(borderColorId ?? '', customBorderColor ?? ''),
    };
  };

  type ColorFieldName = keyof Pick<
    PurchaseNotificationFormValues,
    | 'privateBackgroundColor'
    | 'customPrivateBackgroundColor'
    | 'privateBorderColor'
    | 'customPrivateBorderColor'
    | 'privateTextColor'
    | 'privateBackgroundIcon'
    | 'customPrivateBackgroundIcon'
    | 'privateIconColor'
    | 'customPrivateIconColor'
    | 'publicBackgroundColor'
    | 'customPublicBackgroundColor'
    | 'publicBorderColor'
    | 'customPublicBorderColor'
    | 'publicTextColor'
    | 'publicBackgroundIcon'
    | 'customPublicBackgroundIcon'
    | 'publicIconColor'
    | 'customPublicIconColor'
  >;
  const getFinalColor = (fieldName: string) => {
    const colorId = purchaseNotificationForm.watch(fieldName as ColorFieldName) as string;
    const customField =
      `custom${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}` as ColorFieldName;
    const customColor = purchaseNotificationForm.watch(customField) as string | undefined;
    return getColorValueById(colorId ?? '', customColor);
  };

  const getTextColor = (): string =>
    (isPublic
      ? purchaseNotificationForm.watch('publicTextColor')
      : purchaseNotificationForm.watch('privateTextColor')) || '#FFFFFF';
  const getIconBackgroundColor = () =>
    getFinalColor(isPublic ? 'publicBackgroundIcon' : 'privateBackgroundIcon');
  const getIconColor = () => getFinalColor(isPublic ? 'publicIconColor' : 'privateIconColor');

  const placeholders = [
    t('popupForm.placeholders.firstName'),
    t('popupForm.placeholders.productName'),
    t('popupForm.placeholders.closerName'),
    t('popupForm.placeholders.appointmentTime'),
    t('popupForm.placeholders.appointmentDate'),
    t('popupForm.placeholders.appointmentDay'),
    t('popupForm.placeholders.amount'),
    t('popupForm.placeholders.currency'),
  ];

  const offerType = isPublic ? ('B' as const) : ('A' as const);

  return (
    <Accordion type="single" collapsible defaultValue="item-1">
      <AccordionItem value="item-1">
        <div className="space-y-4">
          <AccordionTrigger>
            <Title level={2}>
              {isPublic ? t('popupForm.publicSectionTitle') : t('popupForm.privateSectionTitle')}{' '}
              <span className="text-base text-tertiary ">
                {isPublic
                  ? t('popupForm.publicSectionSubtitle')
                  : t('popupForm.privateSectionSubtitle')}
              </span>
            </Title>
          </AccordionTrigger>

          <AccordionContent className="space-y-4">
            {(isPublic ? purchaseNotificationForm.watch('publicVariants') : [currentVariant]).map(
              (variant, variantIndex) => (
                <div key={variant.id} className="space-y-4">
                  {isPublic && purchaseNotificationForm.watch('publicVariants').length > 1 && (
                    <div className="flex items-center justify-between">
                      <p></p>
                      {/* <p className="text-sm font-medium">Variante {variantIndex + 1}</p> */}
                      {purchaseNotificationForm.watch('publicVariants').length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeVariant(variant.id)}
                          className="text-red-500 hover:text-red-700 text-sm cursor-pointer"
                          aria-label={t('popupForm.deleteVariantAriaLabel', {
                            index: variantIndex + 1,
                          })}
                        >
                          {t('popupForm.deleteVariant')}
                        </button>
                      )}
                    </div>
                  )}

                  <VariableChunkEditor
                    chunks={variant.chunks as VCEChunk[]}
                    onTextChange={(chunkId, val) => updateTextChunk(chunkId, val, variant.id)}
                    onFieldRemove={(fieldId) => removeField(fieldId, variant.id)}
                    onBackspaceOnEmpty={(chunkId) =>
                      removePreviousFieldWhenChunkEmpty(chunkId, variant.id)
                    }
                    onAddVariable={(v) => addField(v.value, variant.id)}
                    variables={placeholders.map((p) => ({ label: p, value: p }))}
                    variantLabel={t('popupForm.messageLabel', { index: variantIndex + 1 })}
                    hint={t('popupForm.variableHint')}
                    maxLength={150}
                    currentLength={lengthFn ? lengthFn(variant.chunks as VCEChunk[]) : undefined}
                    error={
                      isPublic
                        ? getVariantError(variantIndex)
                        : (privateChunksError as string | undefined)
                    }
                  />
                </div>
              ),
            )}

            {isPublic && purchaseNotificationForm.watch('publicVariants').length < 4 && (
              <button
                type="button"
                onClick={addVariant}
                disabled={variants.length >= 4}
                className="mt-2 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="size-6" />
                <Label className="cursor-pointer text-base">
                  {t('popupForm.addVariant')}{' '}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-tertiary">
                        <CircleAlert size={24} className="text-tertiary" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p>{t('popupForm.addVariantTooltip')}</p>
                    </TooltipContent>
                  </Tooltip>
                </Label>
              </button>
            )}

            <div className="py-2 space-y-4">
              <div className="flex items-center justify-between ">
                <Label>{t('popupForm.showIcon')}</Label>
                <Switch
                  checked={
                    isPublic
                      ? purchaseNotificationForm.watch('displayPublicIcon') || false
                      : purchaseNotificationForm.watch('displayPrivateIcon') || false
                  }
                  onCheckedChange={(checked) => {
                    if (isPublic) {
                      updateAllVariantsShowIcon(checked);
                    } else {
                      updateVariantShowIcon(currentVariant.id, checked);
                    }
                  }}
                />
              </div>

              <div>
                <Controller
                  name={isPublic ? 'publicIcon' : 'privateIcon'}
                  control={purchaseNotificationForm.control}
                  render={({ field: iconField }) => (
                    <LucideIconPopoverPicker
                      value={iconField.value as any}
                      onChange={(icon) => {
                        const iconValue = icon === undefined ? '' : icon;
                        iconField.onChange(iconValue);
                        if (isPublic) {
                          updateAllVariantsIcon(iconValue);
                        }
                        // Pour le cas privé, iconField.onChange gère déjà la mise à jour
                      }}
                    />
                  )}
                />
              </div>
            </div>

            <div className="flex flex-col gap-4 pt-8">
              <Title level={2}>{t('popupForm.colors')}</Title>

              <div className="flex flex-col gap-4 ">
                <div className="flex flex-row lg:flex-col items-center gap-4 w-full">
                  <div className="w-1/2 lg:w-full">
                    <ColorSelector
                      form={purchaseNotificationForm}
                      offerType={offerType}
                      colorsArray={colorsArray}
                      colorFieldName={`${prefix}BackgroundColor`}
                      customColorFieldName={`custom${prefix.charAt(0).toUpperCase() + prefix.slice(1)}BackgroundColor`}
                      label={t('popupForm.backgroundColor')}
                      customColorLabel={t('popupForm.customColor')}
                      getFinalColor={getFinalColor}
                      getStyle={getBackgroundStyle}
                    />
                  </div>
                  <div className="w-1/2 lg:w-full">
                    <ColorSelector
                      form={purchaseNotificationForm}
                      offerType={offerType}
                      colorsArray={colorsArray}
                      colorFieldName={`${prefix}BorderColor`}
                      customColorFieldName={`custom${prefix.charAt(0).toUpperCase() + prefix.slice(1)}BorderColor`}
                      label={t('popupForm.borderColor')}
                      customColorLabel={t('popupForm.customColor')}
                      noBorderLabel={t('popupForm.noBorder')}
                      showNoBorder={true}
                      getFinalColor={getFinalColor}
                      getStyle={(colorId, customColor) => {
                        if (colorId === 'no-border') {
                          return { backgroundColor: 'white' };
                        }
                        return getBackgroundStyle(colorId, customColor);
                      }}
                    />
                  </div>
                </div>

                <ColorSelector
                  form={purchaseNotificationForm}
                  offerType={offerType}
                  colorsArray={arrayFontColors.map((color) => ({
                    id: color.value,
                    name: color.name,
                    value: color.value,
                  }))}
                  colorFieldName={`${prefix}TextColor`}
                  label={t('popupForm.textColor')}
                  showCustomColor={false}
                  // defaultValue="#FFFFFF"
                />

                <div className="flex flex-row lg:flex-col items-center gap-4 w-full">
                  <div className="w-1/2 lg:w-full">
                    <ColorSelector
                      form={purchaseNotificationForm}
                      offerType={offerType}
                      colorsArray={colorsArray}
                      colorFieldName={`${prefix}BackgroundIcon`}
                      customColorFieldName={`custom${prefix.charAt(0).toUpperCase() + prefix.slice(1)}BackgroundIcon`}
                      label={t('popupForm.iconBackgroundColor')}
                      customColorLabel={t('popupForm.customColor')}
                      getFinalColor={getFinalColor}
                      getStyle={getBackgroundStyle}
                      // defaultValue="black"
                    />
                  </div>
                  <div className="w-1/2 lg:w-full">
                    <ColorSelector
                      form={purchaseNotificationForm}
                      offerType={offerType}
                      colorsArray={colorsArray}
                      colorFieldName={`${prefix}IconColor`}
                      customColorFieldName={`custom${prefix.charAt(0).toUpperCase() + prefix.slice(1)}IconColor`}
                      label={t('popupForm.iconColor')}
                      customColorLabel={t('popupForm.customColor')}
                      getFinalColor={getFinalColor}
                      getStyle={getBackgroundStyle}
                      // defaultValue="black"
                    />
                  </div>
                </div>
              </div>
            </div>

            <Accordion type="single" collapsible defaultValue="preview-1">
              <AccordionItem value="preview-1">
                <AccordionTrigger>{t('popupForm.preview')}</AccordionTrigger>
                <AccordionContent className="space-y-4">
                  {(isPublic
                    ? purchaseNotificationForm.watch('publicVariants')
                    : [currentVariant]
                  ).map((variant, index) => (
                    <div key={`preview-${variant.id}`}>
                      {isPublic && purchaseNotificationForm.watch('publicVariants').length > 1 && (
                        <p className="text-sm font-medium mb-2">
                          {t('popupForm.variant', { index: index + 1 })}
                        </p>
                      )}
                      <div
                        className={`flex items-start justify-between mx-auto gap-2 p-4 rounded-[20px] max-w-[408px] ${
                          borderColorId === 'no-border' ? '' : 'border'
                        }`}
                        style={{
                          ...getBackgroundStyle(),
                          ...getBorderStyle(),
                        }}
                      >
                        {variant.showIcon && (
                          <div
                            className="p-2 rounded-lg shrink-0"
                            style={{ backgroundColor: getIconBackgroundColor() }}
                          >
                            {(() => {
                              const iconValue = isPublic
                                ? purchaseNotificationForm.watch('publicIcon') ||
                                  variant.selectedIcon
                                : purchaseNotificationForm.watch('privateIcon') ||
                                  variant.selectedIcon;
                              const isLucideIcon =
                                iconValue &&
                                typeof iconValue === 'string' &&
                                iconValue in dynamicIconImports;
                              const iconColor = getIconColor();

                              return isLucideIcon ? (
                                <LucideLazyIcon
                                  name={iconValue as any}
                                  width={24}
                                  height={24}
                                  color={iconColor}
                                />
                              ) : null;
                            })()}
                          </div>
                        )}
                        <div>
                          <p className="text-base" style={{ color: getTextColor() }}>
                            {getFullMessage(variant.chunks)}
                          </p>
                        </div>
                        <div>
                          <X className="size-4" style={{ color: getTextColor() }} />{' '}
                        </div>
                      </div>
                    </div>
                  ))}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </AccordionContent>
        </div>
      </AccordionItem>
    </Accordion>
  );
}
